import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Award, Copy, Gift, Share2, Users } from 'lucide-react';
import { getReferralSummary } from '../../services/referrals';

const ReferralPage = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const copiedTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setSummary(await getReferralSummary());
      } catch (err) {
        console.error('Failed to load referral summary', err);
        setError('We could not load your referral program details right now.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const copyReferralLink = async () => {
    if (!summary?.referralLink) return;
    await navigator.clipboard.writeText(summary.referralLink);
    setShowCopiedMessage(true);
    copiedTimeoutRef.current = setTimeout(() => setShowCopiedMessage(false), 2000);
  };

  const stats = summary
    ? [
        { label: 'Total Referrals', value: summary.totalReferrals, icon: Users, color: 'text-blue-600 dark:text-blue-400' },
        { label: 'Awaiting Verification', value: summary.pendingVerificationReferrals, icon: Users, color: 'text-amber-600 dark:text-amber-400' },
        { label: 'Awaiting Purchase', value: summary.pendingPurchaseReferrals, icon: Award, color: 'text-indigo-600 dark:text-indigo-400' },
        { label: 'Credits Earned', value: `$${summary.totalCreditIssued.toFixed(2)}`, icon: Gift, color: 'text-green-600 dark:text-green-400' },
      ]
    : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md flex items-start">
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="ml-3">
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Referral Link */}
      <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 flex items-center">
          <Share2 className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
          Your Referral Link
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Share your referral link. New customers automatically get $100 off their first paid subscription, and once that payment succeeds you earn a $100 account credit toward your next invoice.
        </p>
        <div className="relative">
          <input
            type="text"
            value={summary.referralLink}
            readOnly
            className="block w-full px-4 pr-12 bg-gray-50 dark:bg-gray-700 sm:text-sm border border-gray-400 dark:border-gray-600 rounded-md shadow-sm text-gray-900 dark:text-white transition-colors h-11"
          />
          <button
            type="button"
            onClick={copyReferralLink}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
            title="Copy referral link"
          >
            <Copy className="h-5 w-5" />
          </button>
          {showCopiedMessage && (
            <div className="absolute right-10 top-1/2 -translate-y-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded shadow-lg">
              Copied!
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Referral token: <span className="font-mono text-gray-900 dark:text-gray-200">{summary.referralToken}</span>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Available balance: <span className="font-semibold text-green-700 dark:text-green-300">${summary.availableCreditBalance.toFixed(2)}</span>
          </p>
        </div>
      </div>

      {summary.referredDiscountEligible && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 px-4 py-3 rounded-md">
          <p className="text-sm">
            A referral benefit is attached to your account. Your next first-time subscription checkout will automatically include ${summary.referredDiscountAmount.toFixed(2)} off.
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            <div className="mt-1.5 flex items-center min-w-0">
              <Icon className={`h-4 w-4 mr-1.5 flex-shrink-0 ${color}`} />
              <p className="text-xs text-gray-600 dark:text-gray-400 truncate" title={label}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Referral History */}
      <div className="bg-gray-300 dark:bg-gray-700/40 rounded-lg p-5 border border-gray-400 dark:border-gray-600">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Referral History</h3>
        {summary.history.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 py-3">
            No referrals yet. Share your link to get started.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-400 dark:divide-gray-600">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Company</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Attributed</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Reward</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-400 dark:divide-gray-600">
                {summary.history.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2.5 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{item.referredEmail}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{item.referredCompanyName || '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{item.status}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{new Date(item.attributedAt).toLocaleDateString()}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">${item.rewardCreditAmount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
